// src/contexts/DocumentCitationContext.tsx

import React, { createContext, useContext, useMemo } from "react";
import { DocumentCitation } from "../interfaces/documentCitationTypes";

/**
 * Context for looking up document citation data by reference.
 * Used by DocumentCitationBubble to resolve citation details for tooltip display.
 */
interface DocumentCitationContextType {
  /** Look up citation data by ref. Returns undefined if not found. */
  lookupCitation: (ref: string) => DocumentCitation | undefined;
}

const DocumentCitationContext = createContext<DocumentCitationContextType | undefined>(undefined);

interface DocumentCitationProviderProps {
  children: React.ReactNode;
  citations: Record<string, DocumentCitation>;
}

/**
 * Provider that exposes a ref->citation lookup from document citation data.
 * Wrap around MarkdownRenderer content so DocumentCitationBubble can access citation data.
 */
export const DocumentCitationProvider: React.FC<DocumentCitationProviderProps> = ({
  children,
  citations,
}) => {
  const lookupCitation = useMemo(() => {
    return (ref: string): DocumentCitation | undefined => {
      return citations[ref];
    };
  }, [citations]);

  return (
    <DocumentCitationContext.Provider value={{ lookupCitation }}>
      {children}
    </DocumentCitationContext.Provider>
  );
};

/**
 * Hook to access document citation lookup.
 * Returns a no-op lookup if used outside provider (graceful fallback).
 */
export const useDocumentCitations = (): DocumentCitationContextType => {
  const context = useContext(DocumentCitationContext);
  if (!context) {
    return { lookupCitation: () => undefined };
  }
  return context;
};

export default DocumentCitationContext;
